/* ==========================================================================
   Titan Cart Helper
   Shared AJAX add-to-cart + cart drawer rendering for all Titan sections.

   Why this exists: /cart/add.js must request the cart drawer section in the
   same call, then swap the returned HTML into the live drawer BEFORE opening
   it. Opening the drawer on a timer without re-rendering shows stale
   (usually empty) markup from initial page load.

   Requires sections/cart-drawer.liquid to exist — the Section Rendering API
   resolves section names against sections/, not snippets/. Without it,
   add.js returns no drawer HTML and the drawer renders blank.

   Also hosts the delegated quick-add listener for the cart cross-sell.
   That listener CANNOT live in the drawer markup: the drawer's innerHTML is
   replaced on every add, and scripts inserted via innerHTML do not execute.
   ========================================================================== */
(function () {
  'use strict';

  if (window.TitanCart) return;

  var TitanCart = {};

  /* ---------- helpers ---------- */

  function addUrl() {
    var base = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return base.replace(/\.js$/, '') + '.js';
  }

  function sectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  // Only request sections that actually exist in the DOM, so we never ask
  // Shopify to render something this theme doesn't have.
  function requestedSections() {
    var ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    if (document.querySelector('cart-notification')) ids.push('cart-notification');
    return ids;
  }

  function innerHTMLOf(html, selector) {
    if (!html) return null;
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(selector || '.shopify-section');
      return el ? el.innerHTML : null;
    } catch (e) {
      return null;
    }
  }

  function updateCartBubble(state) {
    if (!state || !state.sections || !state.sections['cart-icon-bubble']) return;
    var live = document.getElementById('cart-icon-bubble');
    if (!live) return;
    var fresh = innerHTMLOf(state.sections['cart-icon-bubble']);
    if (fresh !== null) live.innerHTML = fresh;
  }

  /**
   * Clear every is-empty marker in the drawer.
   *
   * When the last line item is removed, Dawn re-renders the drawer in its
   * empty state and stamps `is-empty` onto <cart-drawer>, .drawer__inner,
   * and <cart-drawer-items>. Adding an item afterwards swaps fresh innerHTML
   * in, but innerHTML replaces CHILDREN — the classes on those containers
   * survive. Dawn's stylesheet hides the contents off `is-empty`, so the
   * table is present in the DOM and invisible.
   *
   * The leftover .drawer__inner-empty block is removed for the same reason.
   */
  function clearEmptyState(drawer) {
    if (!drawer) return;

    drawer.classList.remove('is-empty');

    var marked = drawer.querySelectorAll('.is-empty');
    for (var i = 0; i < marked.length; i++) {
      marked[i].classList.remove('is-empty');
    }

    var emptyBlock = drawer.querySelector('.drawer__inner-empty');
    if (emptyBlock && emptyBlock.parentNode) {
      emptyBlock.parentNode.removeChild(emptyBlock);
    }
  }

  /* ---------- drawer rendering ---------- */

  function renderDrawer(state) {
    var drawer = document.querySelector('cart-drawer');

    if (drawer) {
      // Preferred path: hand off to Dawn's own renderer.
      if (state && state.sections && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(state);
          // renderContents opens the drawer itself, but does not reliably
          // clear is-empty when coming from a fully emptied cart.
          clearEmptyState(drawer);
          return true;
        } catch (e) {
          console.warn('[TitanCart] renderContents failed, falling back.', e);
        }
      }

      // Fallback: swap the markup ourselves, then open.
      if (state && state.sections && state.sections['cart-drawer']) {
        var fresh = innerHTMLOf(state.sections['cart-drawer'], '#CartDrawer');
        var live = document.querySelector('#CartDrawer');
        if (fresh !== null && live) live.innerHTML = fresh;
      }

      clearEmptyState(drawer);
      updateCartBubble(state);

      if (typeof drawer.open === 'function') {
        drawer.open();
      } else {
        drawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }
      return true;
    }

    // Theme is set to cart notification instead of drawer.
    var notification = document.querySelector('cart-notification');
    if (notification && typeof notification.renderContents === 'function') {
      try {
        notification.renderContents(state);
        return true;
      } catch (e) {
        console.warn('[TitanCart] cart-notification render failed.', e);
      }
    }

    updateCartBubble(state);
    return false;
  }

  /* ---------- public API ---------- */

  /**
   * Add a line item and open the cart drawer once it has actually rendered.
   * @param {Object} item - { id, quantity, selling_plan? }
   * @returns {Promise<Object>} resolves with the parsed cart state
   */
  TitanCart.add = function (item) {
    var body = {
      items: [item],
      sections_url: sectionsUrl()
    };

    var sections = requestedSections();
    if (sections.length) body.sections = sections.join(',');

    return fetch(addUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            var msg = data && (data.description || data.message)
              ? (data.description || data.message)
              : 'Cart error: ' + res.status;
            var err = new Error(msg);
            err.status = res.status;
            err.data = data;
            throw err;
          }
          return data;
        });
      })
      .then(function (state) {
        // Dawn's renderers read parsedState.id for focus management.
        if (!state.id && state.items && state.items.length) {
          state.id = state.items[0].id;
        }

        var opened = renderDrawer(state);

        document.dispatchEvent(
          new CustomEvent('titan:cart:updated', { detail: { state: state, opened: opened } })
        );

        // Last resort: if there is no drawer and no notification component
        // at all, send the customer to the cart page rather than leaving
        // them with no feedback.
        if (!opened && !document.querySelector('cart-drawer') && !document.querySelector('cart-notification')) {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }

        return state;
      });
  };

  /* ---------- delegated quick-add (cart cross-sell) ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-titan-quick-add]') : null;
    if (!btn) return;

    e.preventDefault();
    if (btn.disabled) return;

    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    var wrapper = btn.closest('[data-titan-cart-upsell]');
    var isCartPage = wrapper && wrapper.getAttribute('data-context') === 'page';

    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    TitanCart.add({ id: variantId, quantity: 1 })
      .then(function () {
        // On the cart page the line items are server-rendered, so reload to
        // reflect the new item. In the drawer, the markup has already been
        // replaced and this button no longer exists.
        if (isCartPage) {
          window.location.reload();
          return;
        }
        btn.classList.add('titan-cart-upsell__add--done');
        btn.textContent = 'Added';
      })
      .catch(function (err) {
        console.error('[Titan Cart Upsell] Quick add failed:', err);
        btn.disabled = false;
        btn.textContent = original;
      });
  });

  window.TitanCart = TitanCart;
})();